import React, { useCallback, useState } from 'react';
import { LoadableView } from 'ui-components/src';
import { DocumentLink } from '../DocumentLink';
import {
  Container,
  EmptyState,
  FolderButton,
  FolderCaret,
  FolderItem,
  FolderName,
  NestedList,
  StyledIconButton,
  StyledList,
} from './styles';
import { AttachmentUploadModal } from 'stateful-ui/src/components/AttachmentUploadModal';
import {
  ATTACHMENTS_APPLICATION,
  ATTACHMENTS_ID,
  ATTACHMENTS_TYPE,
} from 'app/attachments/constants';
import { useCommunityAttachments } from 'app/attachments/hooks/useCommunityAttachments';
import { FileIcon } from 'ui-components/src/components/atoms/icons/FileIcon';
import { useRoleGuardContext } from 'stateful-ui/src/providers/RoleGuardContext';
import { useCommunityAttachmentsUpdatedSubscription } from 'app/attachments/hooks/useCommunityAttachmentsUpdatedSubscription';
import { useFileDownload } from 'stateful-ui/src/hooks/useFileDownload';
import { useToasts } from 'stateful-ui/src/graphql/toasts/useToasts';
import { useDeleteCommunityAttachment } from 'app/attachments/hooks/useDeleteCommunityAttachment';
import { useUpdateAttachmentPath } from 'app/attachments/hooks/useUpdateAttachmentPath';
import { Attachment, Folder } from 'generated/gql';

const collectFolderPaths = (
  folders?: Folder[] | null,
  parentPath?: string,
): string[] =>
  folders?.flatMap((folder) => {
    const currentPath = parentPath
      ? `${parentPath}/${folder.name}`
      : folder.name;

    return [currentPath, ...collectFolderPaths(folder.folders, currentPath)];
  }) || [];

export const DocumentsList = () => {
  const { data, loading, error } = useCommunityAttachments();
  const { pushToast } = useToasts();
  useCommunityAttachmentsUpdatedSubscription();
  const [uploadActive, setUploadActive] = useState(false);
  const { userHasRole } = useRoleGuardContext();
  const { download } = useFileDownload((err) =>
    pushToast({ variables: { toast: { message: err } } }),
  );
  const { deleteFile } = useDeleteCommunityAttachment();
  const { updateAttachmentPath } = useUpdateAttachmentPath();
  const [collapsedFolders, setCollapsedFolders] = useState<
    Record<string, boolean>
  >({});
  const isCommunityAdmin = userHasRole('community-admin');
  const folderOptions = Array.from(new Set(collectFolderPaths(data?.folders)));

  const toggleFolder = useCallback((folderId: string) => {
    setCollapsedFolders((current) => ({
      ...current,
      [folderId]: !(current[folderId] ?? true),
    }));
  }, []);

  const renderAttachment = useCallback(
    (attachment: Attachment) => (
      <DocumentLink
        key={attachment.id}
        label={attachment.name}
        documentStatus={attachment.status}
        onDownload={async () =>
          download(attachment.url, attachment.name, attachment.mimeType)
        }
        onDelete={isCommunityAdmin ? () => deleteFile(attachment) : undefined}
        onUpdatePath={
          isCommunityAdmin
            ? async (path) => {
                await updateAttachmentPath(attachment.id, path);
              }
            : undefined
        }
        existingFolders={folderOptions}
        folderPath={attachment.folderPath}
      />
    ),
    [
      deleteFile,
      download,
      folderOptions,
      isCommunityAdmin,
      updateAttachmentPath,
    ],
  );

  const renderFolders = useCallback(
    (folders?: Folder[] | null, level = 1): React.ReactNode =>
      folders?.map((folder) => {
        const isCollapsed = collapsedFolders[folder.id] ?? true;

        return (
          <FolderItem key={folder.id}>
            <FolderButton type="button" onClick={() => toggleFolder(folder.id)}>
              <FolderName>{folder.name}</FolderName>
              <FolderCaret $collapsed={isCollapsed} />
            </FolderButton>
            {!isCollapsed && (
              <NestedList>
                {renderFolders(folder.folders, level + 1)}
                {folder.attachments?.map(renderAttachment)}
              </NestedList>
            )}
          </FolderItem>
        );
      }),
    [collapsedFolders, renderAttachment, toggleFolder],
  );

  const hasDocuments = !!data?.attachments?.length || !!data?.folders?.length;

  return (
    <Container>
      <LoadableView
        loading={!!loading}
        error={error ? 'Something went wrong' : ''}
      >
        <StyledList>
          {renderFolders(data?.folders)}
          {data?.attachments.map(renderAttachment)}
          {!hasDocuments && (
            <EmptyState>There are no documents available</EmptyState>
          )}
        </StyledList>
        {uploadActive && (
          <AttachmentUploadModal
            scope={{
              application: ATTACHMENTS_APPLICATION,
              parentType: ATTACHMENTS_TYPE,
              parentId: ATTACHMENTS_ID,
              community: true,
            }}
            onClose={() => setUploadActive(false)}
            onUploadComplete={() => {
              setUploadActive(false);
            }}
          />
        )}
        {isCommunityAdmin && (
          <StyledIconButton
            simple
            label="Add document(s)"
            icon={<FileIcon />}
            onClick={() => setUploadActive(true)}
          />
        )}
      </LoadableView>
    </Container>
  );
};
